import React from 'react'
import { useIntl } from 'react-intl'
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { capitalize } from '../../../utils/functions'
import { money } from '../../../utils/salaryFormulas'
import { dateRange } from '../../../utils/periodDates'
import { directorApi, DirReminder } from '../../../features/director/directorApi'
import { useHeadIndex } from '../../head/headData'
import { TONE, bodyRow, headRow } from '../ui'
import { ui } from '../../../utils/uiTokens'

const LOOK = {
    LATE: { tone: TONE.bad, icon: '⚠️', title: 'dirRemLate', days: 'dirRemLateDays' },
    DUE: { tone: TONE.warn, icon: '🔔', title: 'dirRemDue', days: 'dirRemDueDays' },
    SOON: { tone: TONE.info, icon: '📅', title: 'dirRemSoon', days: '' },
}

// Ескертулер на «Жалақы»: мерзімі өтті (қызыл), беру керек (сары), жақында (көк)
const RemindersPanel = () => {
    const { formatMessage, locale } = useIntl()
    const index = useHeadIndex()
    const { data } = directorApi.useGetRemindersQuery()
    const list = data?.reminders ?? []
    const t = (r: { [k: string]: any }, base: string) => r[`${base}${capitalize(locale)}`] || r[`${base}Ru`]
    const names = (list: { id: number; name: string | null }[]) =>
        list.map((m) => m.name || index.mentors.find((x) => x.mentorId === m.id)?.name || `#${m.id}`).join(', ')

    if (!list.length) {
        return <Box sx={{ ...box(TONE.good), marginBottom: '14px' }}>✅ {formatMessage({ id: 'dirRemNone' })}</Box>
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, marginBottom: '16px' }}>
            {list.map((r: DirReminder) => {
                const look = LOOK[r.type] ?? LOOK.SOON
                const head = `${t(r, 'courseTitle')} · ${r.monthNumber} ${formatMessage({ id: 'month' }).toLowerCase()}`
                return (
                    <Box key={`${r.type}:${r.courseId}:${r.monthNumber}`} sx={box(look.tone)}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                            <span>
                                {look.icon} <b>{formatMessage({ id: look.title })}</b> · <b>{head}</b>
                                {r.type === 'SOON' && ` · ${r.rows.map((x) => t(x, 'subjectTitle')).join(', ')}`}
                            </span>
                            <span>
                                {look.days && <b>{formatMessage({ id: look.days }, { n: r.workDays ?? 0 })} </b>}
                                <span style={{ opacity: 0.8 }}>· {formatMessage({ id: 'dirPayWindow' })} {dateRange(r.payFrom, r.payTo)}</span>
                            </span>
                        </Box>
                        {r.type !== 'SOON' && r.rows.length > 0 && (
                            <Box sx={{ overflowX: 'auto', marginTop: '8px', background: ui.card, borderRadius: '10px' }}>
                                <Table size="small" sx={{ minWidth: 560 }}>
                                    <TableHead sx={headRow}>
                                        <TableRow>
                                            <TableCell>{formatMessage({ id: 'colSubject' })}</TableCell>
                                            <TableCell>{formatMessage({ id: 'group' })}</TableCell>
                                            <TableCell>{formatMessage({ id: 'dirUnpaidMentors' })}</TableCell>
                                            <TableCell align="right">{formatMessage({ id: 'dirAmount' })}</TableCell>
                                            <TableCell align="center">{formatMessage({ id: 'approvalApproved' })}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {r.rows.map((x) => (
                                            <TableRow key={`${x.subjectId}:${x.groupId ?? 0}`} sx={bodyRow}>
                                                <TableCell><b>{t(x, 'subjectTitle')}</b></TableCell>
                                                <TableCell sx={{ color: `${ui.sub} !important` }}>{x.groupTitle}</TableCell>
                                                <TableCell>{names(x.unpaidMentors ?? [])}</TableCell>
                                                <TableCell align="right"><b>{money(x.amount ?? 0)}</b></TableCell>
                                                <TableCell align="center">{x.approved ?? 0}/{x.total ?? 0}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                )
            })}
        </Box>
    )
}

const box = (tone: { bg: string; text: string; border: string }) => ({
    background: tone.bg,
    border: `1px solid ${tone.border}`,
    color: tone.text,
    borderRadius: '12px',
    padding: '10px 14px',
    fontSize: '13px',
})

export default RemindersPanel
